/**
 * The till.
 *
 * The screen this whole system exists for, and the one used most. Three states,
 * in order: no shift open, selling, and taking payment.
 *
 * ONE SCREEN, NOT TWO
 * -------------------
 * The old interface had separate admin and cashier tills that had drifted
 * apart, so a fix to one silently left the other wrong. There is one here, and
 * what differs by role is which buttons appear — not which code runs.
 *
 * WHY THE CART IS CLEARED ONLY AFTER THE SERVER ANSWERS
 * -----------------------------------------------------
 * Clearing on tap feels faster and is wrong. If the order fails — the branch
 * machine is unreachable, a product went inactive mid-order — the cashier is
 * left facing a customer with an empty screen and no way to recover what was
 * rung up. The order is placed first; only then does the cart empty.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';

import { api, withQuery } from '../../lib/api.js';
import { useCart } from './cart.js';
import { useShift } from './useShift.js';
import { useQuote } from './useQuote.js';
import { ProductGrid } from './ProductGrid.jsx';
import { ChoicePicker } from './ChoicePicker.jsx';
import { DealPicker } from './DealPicker.jsx';
import { CartPanel } from './CartPanel.jsx';
import { PaymentSheet } from './PaymentSheet.jsx';
import { DiscountPanel } from './DiscountPanel.jsx';
import { OpenShiftPanel } from './OpenShiftPanel.jsx';
import { ShiftClosePanel } from './ShiftClosePanel.jsx';
import { DayClosePanel } from './DayClosePanel.jsx';
import { useBusinessDay } from './useBusinessDay.js';
import { BillSheet } from './BillSheet.jsx';
import { HeldOrdersSheet } from './HeldOrders.jsx';
import { OpenTablesSheet } from './OpenTables.jsx';
import { useHeldOrders } from './useHeldOrders.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { useAuth } from '../../stores/auth.js';

export default function PosPage() {
  const client = useQueryClient();
  const can = useAuth((s) => s.can);
  const signOut = useAuth((s) => s.signOut);
  const { shift, isOpen, loading, openShift, closeShift } = useShift();
  const isCashier = useAuth((s) => s.hasRole)('cashier');
  const { current: businessDay, close: closeDay } = useBusinessDay();
  const { held, loading: heldLoading, hold, recall } = useHeldOrders();

  const [paying, setPaying] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [billFor, setBillFor] = useState(null);
  // Set when a bill is opened by a payment going through, so the receipt prints
  // itself where the shop has asked for that.
  const [autoPrintBill, setAutoPrintBill] = useState(false);

  // Ending a shift is a decision about the day, so it belongs in the bar at
  // the top rather than beside the cart. The shell owns it; this screen just
  // draws the counting when it is happening.
  const { closing, setClosing, closingDay, setClosingDay } = useOutletContext();
  const [showingHeld, setShowingHeld] = useState(false);

  /**
   * Settling a bill somebody else took.
   *
   * `settlingTable` holds the order being paid for while the payment sheet is
   * open, so the sheet knows it is finishing an existing bill rather than
   * charging for what is in the cart.
   */
  const [showingTables, setShowingTables] = useState(false);
  const [settlingTable, setSettlingTable] = useState(null);
  const [choosing, setChoosing] = useState(null);
  // A deal being built — its slots asked for before it drops in the basket.
  const [pickingDeal, setPickingDeal] = useState(null);

  // Deals with their slots, so tapping one knows what to ask. Shares the menu
  // the product grid loaded (same query key), so this reads the cache.
  const menu = useQuery({
    queryKey: ['menu', 'products'],
    queryFn: () =>
      api.get(withQuery('/menu/products', { limit: 500, is_active: true, with_modifiers: 1 })),
    staleTime: 5 * 60_000,
  });
  const deals = menu.data?.deals ?? {};

  const add = useCart((s) => s.add);
  const clear = useCart((s) => s.clear);
  const lines = useCart((s) => s.lines);
  const toOrder = useCart((s) => s.toOrder);
  const setField = useCart((s) => s.setField);
  const orderType = useCart((s) => s.orderType);
  // Set while adding another round to a bill that already exists.
  const addingTo = useCart((s) => s.addingTo);
  const loadOrder = useCart((s) => s.loadOrder);

  // Subscribed so the quote re-runs when any of them moves. They all feed
  // toOrder(), but the screen would not otherwise re-render when they change —
  // and a delivery fee, a zone or a discount that did not re-quote would leave
  // the cashier reading a total that no longer matched the order.
  const deliveryFee = useCart((s) => s.deliveryFee);
  const deliveryZoneId = useCart((s) => s.deliveryZoneId);
  const discount = useCart((s) => s.discount);
  const promotionCode = useCart((s) => s.promotionCode);

  // The amount owed, worked out by the server. The cart can only reach a
  // subtotal; tax belongs to the branch's rules, and the gap is around 16%.
  const order = useMemo(
    () => (lines.length ? toOrder() : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, orderType, deliveryFee, deliveryZoneId, discount, promotionCode, toOrder]
  );
  const { quote, error: quoteError, pending: quoting } = useQuote(order, { enabled: isOpen });

  // The cash and card rates, so the payment screen can show a card total on a
  // bill taken earlier — a fresh order already carries both on its quote.
  const taxRates = useQuery({
    queryKey: ['pos', 'tax-rates'],
    queryFn: () => api.get('/pos/tax-rates'),
    staleTime: 5 * 60_000,
  });
  // Who a bill can be put on the account of — for the payment screen's picker.
  const staff = useQuery({
    queryKey: ['pos', 'staff'],
    queryFn: () => api.get('/pos/staff'),
    staleTime: 5 * 60_000,
  });
  const [discounting, setDiscounting] = useState(false);

  const place = useMutation({
    mutationFn: (payment) => api.post('/pos/orders', { ...toOrder(), ...payment }),
    onSuccess: (result) => {
      const order = result?.order ?? result;

      // Only now is it safe to lose what was rung up.
      clear();
      setPaying(false);
      // A paid sale with auto-print on goes straight to the receipt, which
      // prints itself; otherwise the usual confirmation.
      if (result?.autoPrint) {
        setBillFor(order.id);
        setAutoPrintBill(true);
      } else {
        setPlaced(order);
      }

      // The kitchen screen, the board of open orders and the sync indicator all
      // want to know.
      client.invalidateQueries({ queryKey: ['kitchen'] });
      client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
      client.invalidateQueries({ queryKey: ['sync'] });
    },
  });

  /**
   * Send a dine-in order to the kitchen and leave the bill open.
   *
   * Somebody orders at the counter, goes and sits down, and pays when they have
   * finished. The bill sits on their table until then, exactly as it does for
   * an order a waiter took on the floor — and it is settled by the same screen,
   * from Tables.
   *
   * WHY THIS NEEDS NOTHING FROM THE SERVER
   * --------------------------------------
   * Because an order with no payment on it is not a new idea here: it is what
   * every waiter has always sent. The endpoint books no payment, no journal
   * entry and no takings until somebody pays, the table counts as occupied
   * while the bill is unpaid, and settling later books the sale and the cost of
   * it together. The only thing that was missing was a way for the counter to
   * start one.
   */
  const payLater = useMutation({
    // Deliberately the order alone: no method, no amount, nothing that could be
    // read as money having changed hands.
    mutationFn: () => api.post('/pos/orders', toOrder()),
    onSuccess: (result) => {
      const order = result?.order ?? result;

      clear();
      setPaying(false);
      setPlaced({ ...order, unpaid: true });

      // The floor plan has a new bill on it, the board has one more order, and
      // the kitchen has a ticket.
      client.invalidateQueries({ queryKey: ['floor'] });
      client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
      client.invalidateQueries({ queryKey: ['kitchen'] });
      client.invalidateQueries({ queryKey: ['sync'] });
    },
  });

  /**
   * A Foodpanda order. No money is taken at the counter — the customer paid
   * Foodpanda, which pays the shop out weeks later — so this sends the order and
   * lets the server book it as owed by Foodpanda. It then shows on the Foodpanda
   * tab to be cleared when the payout lands.
   */
  const placeFoodpanda = useMutation({
    // Just the order: the order type is Foodpanda, and the server does the rest.
    mutationFn: () => api.post('/pos/orders', toOrder()),
    onSuccess: (result) => {
      const order = result?.order ?? result;

      clear();
      setPaying(false);
      setPlaced({ ...order, foodpanda: true });

      client.invalidateQueries({ queryKey: ['kitchen'] });
      client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
      client.invalidateQueries({ queryKey: ['foodpanda'] });
      client.invalidateQueries({ queryKey: ['sync'] });
    },
  });

  /**
   * Save a whole edited order.
   *
   * The cart was loaded with the order — every line, its type, its table — and
   * changed however the cashier liked. This sends the whole thing back at once:
   * the server reconciles the lines (so the kitchen sees only what changed),
   * re-prices it, and applies the type or table change. Nothing is created; the
   * order keeps its number.
   */
  const saveEdit = useMutation({
    mutationFn: (approval) =>
      api.put(`/pos/orders/${addingTo.orderId}/revise`, {
        ...toOrder(),
        ...(approval ? { approval } : {}),
      }),
    onSuccess: (result) => {
      const edited = addingTo; // captured before clear() forgets it
      clear();
      setPlaced({
        id: edited?.orderId,
        orderNumber: edited?.orderNumber ?? result?.orderNumber,
        edited: true,
        editedLabel: edited?.label,
      });
      client.invalidateQueries({ queryKey: ['kitchen'] });
      client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
      client.invalidateQueries({ queryKey: ['floor'] });
      client.invalidateQueries({ queryKey: ['sync'] });
    },
  });

  /**
   * Send the edit up for a manager to approve on their own device, instead of
   * a PIN here. The order is not changed now; the whole edit is stored and
   * carried out when a manager approves it. The cart clears, and the "On now"
   * board shows the bill as waiting.
   */
  const sendEditRequest = useMutation({
    mutationFn: () =>
      api.post('/pos/approvals', {
        action: 'revise',
        orderId: addingTo.orderId,
        payload: toOrder(),
      }),
    onSuccess: () => {
      const edited = addingTo;
      clear();
      setPlaced({
        orderNumber: edited?.orderNumber,
        editedLabel: edited?.label,
        sentForApproval: true,
      });
      client.invalidateQueries({ queryKey: ['pos', 'approvals', 'mine'] });
    },
    onError: (e) => {
      // Already waiting is not a failure — the board is showing it. Anything
      // else, keep the edit on screen and say what went wrong.
      if (e?.code === 'ALREADY_PENDING') {
        const edited = addingTo;
        clear();
        setPlaced({ orderNumber: edited?.orderNumber, editedLabel: edited?.label, sentForApproval: true });
      } else {
        window.alert(e?.message ?? 'Could not send the request.');
      }
    },
  });

  /**
   * Open an existing unpaid order in the cart, fully editable, from the board.
   *
   * The whole order loads in — lines, quantities, type, table, customer — and
   * from there it edits exactly like a new one, saved by "Save changes".
   */
  const addMore = async (order) => {
    // A fresh edit starts clean: drop any approval prompt or error left on the
    // last one, so opening a new order never inherits the previous PIN box.
    saveEdit.reset();
    sendEditRequest.reset();
    const data = await api.get(`/pos/orders/${order.id}/for-edit`);
    loadOrder(data);
  };

  /**
   * Backing out of an edit. Empties the cart and, crucially, clears the save
   * mutation — its error is what shows the manager-PIN box, and left behind it
   * would greet the next order the cashier opens with a prompt for a change
   * they never made.
   */
  const cancelEdit = () => {
    saveEdit.reset();
    sendEditRequest.reset();
    clear();
  };

  /**
   * Take payment for a bill that already exists.
   *
   * Separate from `place` because nothing is being created: the order, its
   * items and its total were settled when the waiter sent it. This only adds
   * the money — and frees the table, which follows from the bill being paid.
   */
  const settle = useMutation({
    mutationFn: (payment) => api.post(`/pos/orders/${settlingTable.id}/pay`, payment),
    onSuccess: (result) => {
      const settledId = settlingTable.id;
      setPaying(false);
      setSettlingTable(null);
      setShowingTables(false);
      if (result?.autoPrint) {
        setBillFor(settledId);
        setAutoPrintBill(true);
      } else {
        setPlaced({ ...result, id: settledId, orderNumber: result.orderNumber });
      }

      // The floor plan, the board of open orders, and the day's figures have
      // all moved on.
      client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
      client.invalidateQueries({ queryKey: ['floor'] });
      client.invalidateQueries({ queryKey: ['kitchen'] });
      client.invalidateQueries({ queryKey: ['sync'] });
    },
  });

  /**
   * Park what is on screen.
   *
   * Cleared only after the server has it — the same rule as placing an order.
   * Clearing first feels quicker and loses the basket if the request fails.
   */
  function parkOrder() {
    hold.mutate(
      { orderData: toOrder(), customerName: useCart.getState().customerName || null },
      { onSuccess: () => clear() }
    );
  }

  /**
   * Bring a parked basket back.
   *
   * Rebuilt from the products it names rather than trusted wholesale: prices
   * may have changed while it sat there, and the price on screen must be the
   * one the server will charge.
   */
  function bringBack(id) {
    recall.mutate(id, {
      onSuccess: async (result) => {
        const parked = result?.orderData;
        if (!parked?.items?.length) return;

        clear();
        const menu = await api.get(
          withQuery('/menu/products', { limit: 500, with_modifiers: 1 })
        );
        const byId = new Map((menu.products ?? []).map((p) => [p.id, p]));

        /**
         * The choices come back with it, not just the dishes.
         *
         * A parked order stores only the option ids, so the name and price
         * have to be found again — and they are looked up from the menu as it
         * is now, for the same reason the prices are: what the basket shows
         * must be what the server will charge, not what it would have charged
         * when the order was parked.
         *
         * Without this a table's order came back having quietly lost every
         * size and extra on it, which nobody would notice until the food
         * arrived wrong.
         */
        const optionsById = new Map();
        for (const groups of Object.values(menu.modifierGroups ?? {})) {
          for (const group of groups) {
            for (const option of group.options ?? []) optionsById.set(option.id, option);
          }
        }

        for (const item of parked.items) {
          const product = byId.get(item.productId);
          if (!product) continue;

          const modifiers = (item.modifiers ?? [])
            .map((m) => optionsById.get(m.optionId))
            .filter(Boolean)
            .map((o) => ({
              optionId: o.id,
              name: o.name,
              priceAdjustment: Number(o.price_adjustment) || 0,
            }));

          for (let n = 0; n < (Number(item.quantity) || 1); n++) add(product, { modifiers });
        }

        if (parked.orderType) useCart.getState().setOrderType(parked.orderType);
        if (parked.customerName) setField('customerName', parked.customerName);
        setShowingHeld(false);
      },
    });
  }

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner label="Checking your shift" />
      </div>
    );
  }

  // Closing the day is checked before the shift state, because it does not
  // depend on one: a day can be closed with no shift open, and closing it must
  // not be blocked behind the "open a shift" screen.
  if (closingDay) {
    return (
      <DayClosePanel
        current={businessDay.data}
        busy={closeDay.isPending}
        error={closeDay.error}
        result={closeDay.data}
        onClose={(body) => closeDay.mutate(body)}
        onCancel={() => {
          closeDay.reset();
          setClosingDay(false);
        }}
      />
    );
  }

  // Closing the shift is checked before the shift state, and this order
  // matters: the moment the shift is closed it stops being "open", and if the
  // "open a shift" screen were checked first it would take over — the cashier
  // would never see the count summary or the "Sign out" that hands the till on,
  // and would land straight back on "open a shift", still signed in. That was
  // the bug: the till did not log them out and showed no shift report.
  if (closing) {
    return (
      <ShiftClosePanel
        shift={shift?.shift}
        busy={closeShift.isPending}
        error={closeShift.error}
        result={closeShift.data}
        onClose={(body) => closeShift.mutate(body)}
        onCancel={() => {
          closeShift.reset();
          setClosing(false);
        }}
        // Once the drawer is counted and the shift is closed, the till is done
        // with this person: signing out returns it to the login screen so the
        // next cashier can sign in and open their own shift, without anyone
        // hunting for a logout button first.
        onFinish={() => signOut()}
      />
    );
  }

  if (!isOpen) {
    // Only a cashier opens a shift; everybody else waits for one.
    if (!isCashier) {
      return (
        <div className="mx-auto max-w-md p-6 text-center">
          <h1 className="font-semibold text-xl text-ink">No shift is open</h1>
          <p className="mt-2 text-sm text-ink-2">
            A cashier has to open a shift before bills can be taken. Once one is open, anything you ring up lands on it.
          </p>
        </div>
      );
    }
    return (
      <OpenShiftPanel
        onOpen={(body) => openShift.mutate(body)}
        busy={openShift.isPending}
        error={openShift.error}
      />
    );
  }

  return (
    <div className="relative flex h-[calc(100dvh-3.5rem)]">
      <div className="min-w-0 flex-1">
        <ProductGrid
          onPick={(product, asks) => {
            /**
             * A dish with questions on it opens the picker; everything else
             * drops straight into the basket, as it always has.
             *
             * Which is every dish today, so this must stay out of the way: a
             * till that put a sheet in front of a plain burger would be slower
             * than the one it replaced.
             */
            const deal = deals[product.id];
            if (deal?.slots?.length) {
              // A deal opens the builder when there is anything to ask — a part
              // with more than one option, or a part whose one item has its own
              // choices (a size). One whose every part is a single fixed item
              // just drops in, resolved.
              const groupsFor = (pid) => menu.data?.modifierGroups?.[pid] ?? [];
              const allFixed = deal.slots.every(
                (s) => s.options.length === 1 && groupsFor(s.options[0].productId).length === 0
              );
              if (allFixed) {
                const components = deal.slots.map((s) => ({
                  productId: s.options[0].productId,
                  quantity: s.quantity,
                }));
                add(product, { components });
              } else {
                setPickingDeal({ product, deal });
              }
            } else if (asks?.length) {
              setChoosing({ product, groups: asks });
            } else {
              add(product);
            }
          }}
          /**
           * The board of open orders settles a bill through exactly the same
           * path the Tables sheet does — the same payment screen, the same
           * mutation, the same figure. A second way to take money is a second
           * way to take it wrongly.
           */
          onSettle={(order) => {
            setSettlingTable({
              id: order.id,
              total: order.total_amount,
              number: order.order_number,
            });
            setPaying(true);
          }}
          onBill={(order) => setBillFor(order.id)}
          onAddMore={addMore}
          settling={settle.isPending ? settlingTable?.id : null}
        />
      </div>

      <div className="w-80 shrink-0 lg:w-96">
        <CartPanel
          onDiscount={() => setDiscounting((open) => !open)}
          onPark={parkOrder}
          onShowHeld={() => setShowingHeld(true)}
          heldCount={held.length}
          onShowTables={() => setShowingTables(true)}
          parking={hold.isPending}
          quote={quote}
          quoting={quoting}
          quoteError={quoteError}
          onCharge={() => setPaying(true)}
          onPayLater={() => payLater.mutate()}
          onFoodpanda={() => placeFoodpanda.mutate()}
          addingTo={addingTo}
          onSaveEdit={(approval) => saveEdit.mutate(approval)}
          onCancelEdit={cancelEdit}
          editError={saveEdit.error}
          onSendEditRequest={() => sendEditRequest.mutate()}
          sendingEdit={sendEditRequest.isPending}
          placing={place.isPending || saveEdit.isPending || placeFoodpanda.isPending}
        />
      </div>

      {discounting && (
        <div className="absolute inset-x-0 bottom-0 z-20 mx-auto max-w-lg p-3 sm:right-4 sm:left-auto sm:w-96">
          <DiscountPanel
            subtotal={quote?.subtotal ?? 0}
            orderType={order?.orderType}
            approvalNeeded={quote?.approvalNeeded ?? null}
            onClose={() => setDiscounting(false)}
          />
        </div>
      )}

      {choosing && (
        <ChoicePicker
          product={choosing.product}
          groups={choosing.groups}
          onCancel={() => setChoosing(null)}
          onDone={(modifiers) => {
            add(choosing.product, { modifiers });
            setChoosing(null);
          }}
        />
      )}

      {pickingDeal && (
        <DealPicker
          product={pickingDeal.product}
          deal={pickingDeal.deal}
          modifierGroups={menu.data?.modifierGroups ?? {}}
          onCancel={() => setPickingDeal(null)}
          onDone={(components) => {
            add(pickingDeal.product, { components });
            setPickingDeal(null);
          }}
        />
      )}

      {paying && (
        <PaymentSheet
          // Settling an existing bill prices itself from the order, not from
          // whatever happens to be in the cart.
          quote={
            settlingTable
              ? { totalAmount: settlingTable.total, taxRates: taxRates.data }
              : quote
          }
          orderType={settlingTable ? 'dine_in' : orderType}
          staff={staff.data?.staff ?? []}
          busy={settlingTable ? settle.isPending : place.isPending}
          error={settlingTable ? settle.error : place.error}
          onCancel={() => {
            place.reset();
            settle.reset();
            setPaying(false);
            setSettlingTable(null);
          }}
          onConfirm={(payment) =>
            settlingTable ? settle.mutate(payment) : place.mutate(payment)
          }
        />
      )}

      {showingTables && (
        <OpenTablesSheet
          settling={settle.isPending ? settlingTable?.id : null}
          onClose={() => setShowingTables(false)}
          onSettle={(order) => {
            setSettlingTable(order);
            setPaying(true);
            /**
             * Close this sheet as the payment opens.
             *
             * Both are modals. Left open, this one sits on top of the payment
             * screen and swallows every click on it — so a cashier pressing
             * "Take payment" got two overlapping boxes and no way to finish.
             * Settling a table was impossible.
             *
             * It was closed only in the success handler, which could never run,
             * because reaching it needed the click this sheet was blocking.
             */
            setShowingTables(false);
          }}
        />
      )}

      {placed && (
        <OrderPlaced
          order={placed}
          onDismiss={() => setPlaced(null)}
          onBill={() => {
            setBillFor(placed.id);
            setPlaced(null);
          }}
        />
      )}

      {showingHeld && (
        <HeldOrdersSheet
          held={held}
          loading={heldLoading}
          busy={recall.isPending}
          onRecall={bringBack}
          onClose={() => setShowingHeld(false)}
        />
      )}

      {billFor && (
        <BillSheet
          orderId={billFor}
          canRefund={can('pos.refund.pos')}
          canDiscount={can('pos.create.pos')}
          printOnLoad={autoPrintBill}
          onClose={() => {
            setBillFor(null);
            setAutoPrintBill(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Confirmation that the order went through.
 *
 * Shows the order number, because that is what a customer is told to quote and
 * what the kitchen calls out. Dismisses on tap rather than on a timer — a
 * cashier who looks away for four seconds should not miss it.
 */
function OrderPlaced({ order, onDismiss, onBill }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Order placed"
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/70 p-4"
    >
      <div className="w-full max-w-xs rounded-2xl bg-surface p-6 text-center">
        {/*
          Which of the two it was, said plainly.

          A cashier who has just sent an order to the kitchen without taking any
          money needs to know that is what happened — otherwise the next thing
          they do is wonder whether the payment screen failed.
        */}
        <p
          className={`text-sm font-medium ${
            order?.unpaid || order?.edited || order?.foodpanda || order?.sentForApproval
              ? 'text-warn'
              : 'text-good'
          }`}
        >
          {order?.sentForApproval
            ? `Sent to a manager — ${order.editedLabel ?? order.orderNumber}`
            : order?.edited
              ? `Saved — ${order.editedLabel ?? order.orderNumber} updated`
              : order?.foodpanda
                ? 'Sent to the kitchen — owed by Foodpanda'
                : order?.unpaid
                  ? 'Sent to the kitchen — not paid yet'
                  : 'Order placed'}
        </p>
        <p className="mt-2 text-3xl font-semibold tabular-nums text-ink">
          {order?.orderNumber ?? '—'}
        </p>

        {order?.foodpanda && (
          <p className="mt-2 text-sm text-ink-2">
            Foodpanda pays this out later. It is on the Foodpanda tab, to clear
            when the payment arrives.
          </p>
        )}

        {order?.unpaid && (
          <p className="mt-2 text-sm text-ink-2">
            {order?.order_type === 'dine_in' || order?.orderType === 'dine_in'
              ? 'The bill stays on their table. Settle it from Tables when they have finished.'
              : 'The bill is on the “On now” board, unpaid. Settle it with “Take payment” when the customer pays.'}
          </p>
        )}

        {order?.sentForApproval && (
          <p className="mt-2 text-sm text-ink-2">
            The order is unchanged until a manager approves it. It shows as “waiting” on the
            “On now” board, and you’ll be told here when it is decided.
          </p>
        )}

        <div className="mt-5 flex gap-2">
          {/* Nothing to print for a change that has not happened yet. */}
          {!order?.sentForApproval && (
            <Button variant="secondary" size="lg" className="flex-1" onClick={onBill}>
              Bill
            </Button>
          )}
          {/* Carrying on is the common case, so it is the prominent one — most
              customers do not wait for a printed bill. */}
          <Button size="lg" className="flex-1" onClick={onDismiss} autoFocus>
            {order?.sentForApproval ? 'Done' : 'Next order'}
          </Button>
        </div>
      </div>
    </div>
  );
}
